import dayjs from "dayjs";
import {
  clockicongrey,
  downarrowgrey,
  msgicon,
  recordicon,
  uparrowgrey,
} from "./threadsvg";
import { useState, useRef } from "react";
import { showToast } from "../../../../reduxtoolkit/toastslice";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../../api/api";
import useNavigationHold from "../../../../utils/useNavigationHold";
import { getPracticeId } from "../../../../config/practice";

const TAG_STYLES = {
  "Nurse Call": "bg-[#ECFEFF] text-[#007595] border border-[#A2F4FD]",
  Labs: "bg-[#FAF5FF] text-[#8200DB] border border-[#E9D4FF]",
  "Medication Call": "bg-[#FFFBEB] text-[#E17100] border border-[#FEE685]",
  "Rx Refill": "bg-[#F0FDF4] text-[#008236] border border-[#B9F8CF]",
  "Prior Auth": "bg-[#FEF2F2] text-[#E7000B] border border-[#FFC9C9]",
  Referral: "bg-[#EFF6FF] text-[#1447E6] border border-[#BEDBFF]",
  Callback: "bg-[#FFFBEB] text-[#E17100] border border-[#FEE685]",
  Insurance: "bg-[#ECFEFF] text-[#007595] border border-[#A2F4FD]",
};

const PRIORITY_STYLES = {
  URGENT: "bg-[#FEF2F2] text-[#E7000B] border border-[#FFC9C9]",
  High: "bg-[#FFFBEB] text-[#E17100] border border-[#FEE685]",
  Open: "bg-[#ECFEFF] text-[#007595] border border-[#A2F4FD]",
};

const STATUS_DOT = {
  Open: (
    <span className="w-2 h-2 rounded-full bg-[#FE9A00] inline-block"></span>
  ),
  "In Progress": (
    <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>
  ),
  Closed: (
    <span className="w-2 h-2 rounded-full bg-gray-300 inline-block"></span>
  ),
};
export const formatDate = (dateString) => {
  return dayjs(dateString).format("MMMM D, YYYY");
};
export const formatTime = (dateString) => {
  return dayjs(dateString).format("hh:mm a");
};
const getShortText = (text, limit = 90) => {
  if (!text) return "";
  return text.length > limit ? text.slice(0, limit) + "..." : text;
};
export const formatDateTime = (dateString) => {
  return dayjs(dateString).format("YYYY-MM-DD [at] hh:mm A");
};
const ThreadCard = ({
  data,
  author,
  thread,
  fetchdata,
  users,
  expanded,
  onToggle,

  index,
  resdata = [],
}) => {
  const [showFullText, setShowFullText] = useState(null);
  const categories = [
    "Urgent",
    "Labs",
    "Rx Refill",
    "Referral",
    "Callback",
    "Insurance",
    "Administrative",
  ];
  const statusOptions = [
    "Open",
    "Closed",
    // "Resolved",
    // "Pending",
    // "In Progress",
  ];
  const [replyText, setReplyText] = useState("");
  const [replyCategory, setReplyCategory] = useState("");
  const [threadStatus, setThreadStatus] = useState(thread?.status || "Open");
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showMention, setShowMention] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  // A location switch / expired sign-in waits instead of reloading the page
  // mid-dictation (utils/deferredNavigation).
  useNavigationHold(isRecording || isTranscribing);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const dispatch = useDispatch();
  const userConfig = useSelector((state) => state.userconfig);
  const practiseid = getPracticeId();

  const handleStatusChange = async (newStatus) => {
    const prevStatus = threadStatus;
    setThreadStatus(newStatus);
    try {
      setStatusLoading(true);
      await api.put(
        `/v1/${practiseid}/nonvisitnote/${thread.id}/changestatus/${newStatus}`,
      );
      dispatch(
        showToast({
          message: `Status changed to ${newStatus}`,
          severity: "success",
        }),
      );
      fetchdata({ skipLoader: true });
    } catch (error) {
      console.error("Error changing status:", error);
      setThreadStatus(prevStatus);
      dispatch(
        showToast({
          message: "Failed to change status",
          severity: "error",
        }),
      );
    } finally {
      setStatusLoading(false);
    }
  };
  const match = replyText.match(/@([\w\s.]*)$/);
  const query = match ? match[1].trim().toLowerCase() : "";

  const taggableList = (users || []).map((u) => ({
    id: u.id,
    name: `${u.firstname} ${u.lastname}`.trim(),
    type: u.role || "User",
  }));

  const filteredTaggable = query
    ? taggableList.filter((item) => item.name.toLowerCase().includes(query))
    : [];
  const renderTextWithMentions = (text) => {
    if (!text) return "";

    const names = taggableList.map((item) => item.name).filter(Boolean);
    if (names.length === 0) {
      return text;
    }
    const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const pattern = new RegExp(`(@(?:${escaped.join("|")}))`, "gi");

    return text.split(pattern).map((part, i) => {
      if (part.startsWith("@")) {
        const mentionName = part.slice(1);
        const isKnown = names.some(
          (n) => n.toLowerCase() === mentionName.toLowerCase(),
        );
        if (isKnown) {
          return (
            <span key={i} className="text-blue-600 font-medium cursor-default">
              {part}
            </span>
          );
        }
      }
      return <span key={i}>{part}</span>;
    });
  };
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          const randomString = `audio_${Math.random().toString(36).substring(2, 15)}.webm`;
          formData.append("audio_file", audioBlob, randomString);

          const response = await api.post(
            `/v1/${practiseid}/messaging/speech_to_text`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          setReplyText((prev) => prev + (response?.text || ""));
        } catch (error) {
          console.error("Error calling transcription API:", error);
        } finally {
          setIsTranscribing(false);
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing audio devices:", error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleCreate = async () => {
    if (loading) return;
    // ✅ VALIDATIONS WITH ALERTS
    if (!replyText) {
      dispatch(
        showToast({
          message: "Please enter reply text",
          severity: "warning",
        }),
      );
      return;
    }

    // if (!replyCategory) {
    //   dispatch(
    //     showToast({
    //       message: "Please select a category",
    //       severity: "warning",
    //     })
    //   );
    //   return;
    // }

    try {
      setLoading(true);

      const payload = {
        sender_id: userConfig.id,
        activity_text: replyText,
        category: replyCategory.replace(/\s+/g, ""),
        status: "In-Progress",
        non_visit_note_id: thread?.id,
        recipient_id: taggedUsers.length > 0 ? taggedUsers[0].id : null,
      };

      const response = await api.post(
        `/v1/${practiseid}/patient/${data.patientid}/nonvisitnotes`,
        payload,
      );

      if (response.status === 200) {
        fetchdata({ skipLoader: true });
        setReplyText("");
        setReplyCategory("");
        setTaggedUsers([]);
      }
    } catch (err) {
      dispatch(
        showToast({
          message: "Something Went Wrong",
          severity: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className={`bg-white flex flex-col border border-gray-200
    ${index === 0 ? "rounded-t-xl" : ""}
    ${index === resdata?.length - 1 ? "rounded-b-xl" : ""}
  `}
    >
      <div
        className={`flex items-center gap-3 px-3 py-3.5 ${
          expanded ? "border-b border-gray-200" : ""
        }`}
      >
        {/* Clock icon */}
        <div>{clockicongrey}</div>

        {/* Date/time + author */}
        <div className="w-36 flex-shrink-0 text-start">
          <p className="text-xs font-semi-bold text-[#1D2951] leading-tight">
            {formatDate(thread?.created_at)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatTime(thread?.created_at)} · {author}
          </p>
        </div>

        {/* Tags + Title (FIXED) */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Tags */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* {tags.map((tag) => (
      <span
        key={tag}
        className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${
          TAG_STYLES[tag] ||
          "bg-gray-100 text-gray-500 border border-gray-200"
        }`}
      >
        {tag}
      </span>
    ))} */}
            {thread?.category == ""
              ? null
              : thread?.category?.split(",")?.map((cat, i) => (
                  <span
                    key={i}
                    className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${
                      TAG_STYLES[cat] ||
                      "bg-gray-100 text-gray-500 border border-gray-200"
                    }`}
                  >
                    {cat}
                  </span>
                ))}
            {/* {thread?.status&& (
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${
          PRIORITY_STYLES[thread?.status] || "bg-gray-100 text-gray-500 border border-gray-200"
        }`}
      >
        {thread?.status}
      </span>
    )} */}
          </div>

          {/* Title */}
          <div className="">
            <span
              onClick={() => setShowFullText(thread?.subject)}
              className="block text-sm text-gray-800 font-medium cursor-pointer"
            >
              {getShortText(thread?.subject)}
            </span>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
          {threadStatus && (
            <div className="flex items-center gap-1.5">
              {STATUS_DOT[threadStatus]}

              <span className="text-xs text-gray-400 font-medium">
                {threadStatus}
              </span>
            </div>
          )}

          {thread.replies.length - 1 >= 0 && (
            <div className="flex items-center gap-1 text-gray-400 text-xs">
              {msgicon}
              <span>{thread.replies.length - 1}</span>
            </div>
          )}

          <button
            onClick={onToggle}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            {!expanded ? downarrowgrey : uparrowgrey}
          </button>
        </div>
      </div>
      {expanded && (
        <div className={`px-16 py-4 space-y-4`}>
          {thread.replies?.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-3 text-start items-start ${i > 0 ? "ml-2" : ""}`}
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs text-white font-medium">
                {msg.firstname?.[0]}
              </div>

              {/* Content */}
              <div className="flex-1">
                {i === 0 ? (
                  // ✅ FIRST MESSAGE (NO BOX)
                  <>
                    <div className="flex gap-4 items-center">
                      <span className="text-sm font-medium text-gray-800">
                        {msg.username}
                      </span>
                      <span className="text-xs text-start text-gray-400">
                        {formatDateTime(msg.created_at)}
                      </span>
                    </div>

                    <div className="mt-1">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {renderTextWithMentions(msg.activity_text)}
                      </p>
                    </div>
                  </>
                ) : (
                  // ✅ OTHER MESSAGES (FULL BOX WITH HEADER INSIDE)
                  <div className="bg-[#F9FAFB] border border-gray-200 rounded-xl px-4 py-3 w-full">
                    {/* Header INSIDE box */}
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">
                          {msg.username}
                        </span>
                        {/* <span
     
        className={`text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${
          TAG_STYLES[msg?.category] ||
          "bg-gray-100 text-gray-500 border border-gray-200"
        }`}
      >
        {msg.category}
      </span> */}
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatTime(msg?.created_at)}
                      </span>
                    </div>

                    {/* Message */}
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {renderTextWithMentions(msg.activity_text)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="flex gap-4 items-start mt-4">
            {/* Mic */}
            <div
              className="flex flex-col items-center cursor-pointer"
              onClick={isRecording ? stopRecording : startRecording}
            >
              <div
                className={`w-12 h-12 ${isRecording ? "bg-red-600 animate-pulse" : isTranscribing ? "bg-yellow-500" : "bg-red-500"} rounded-full flex items-center justify-center text-white`}
              >
                {isTranscribing ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  recordicon
                )}
              </div>
              <span className="text-[10px] text-gray-400 mt-1">
                {isRecording
                  ? "Stop"
                  : isTranscribing
                    ? "Transcribing..."
                    : "Record"}
              </span>
            </div>

            {/* Input Box */}
            <div className="flex-1 border border-gray-200 rounded-xl bg-[#f9fafbcc] p-3">
              {/* TEXTAREA (BIG INPUT) */}
              <textarea
                value={replyText}
                onChange={(e) => {
                  const value = e.target.value;
                  setReplyText(value);

                  setShowMention(/@\w*$/.test(value)); // detect @
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleCreate(); // ✅ ENTER submit
                  }
                }}
                // placeholder="@"
                className="w-full h-16 resize-none outline-none text-sm text-gray-700 bg-[#f9fafbcc]"
              />

              {/* Bottom Row */}
              <div className="flex justify-between items-center mt-2">
                <select
                  value={threadStatus}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={statusLoading}
                  className={`text-sm border border-gray-200 rounded-md px-3 py-1 cursor-pointer transition-colors ${
                    statusLoading ? "opacity-50 cursor-not-allowed text-gray-400" : "text-gray-500"
                  } ${threadStatus === "Closed" ? "bg-gray-100 text-gray-400" : ""}`}
                >
                  {statusOptions.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>

                <div className="flex items-center gap-3">
                  {/* <span className="text-xs text-gray-400">⌘ + Enter</span> */}
                  <button
                    onClick={handleCreate}
                    disabled={loading}
                    className={`text-sm px-4 py-1.5 rounded-lg flex items-center gap-2 transition-all
    ${
      loading
        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
        : "bg-[#E17954] text-white hover:opacity-90"
    }`}
                  >
                    {loading && (
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    )}
                    {loading ? "Sending..." : "Reply"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showFullText && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-lg p-5 relative">
            {/* Close button */}
            <button
              onClick={() => setShowFullText(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-lg"
            >
              ✕
            </button>

            {/* Content */}
            <div className="max-h-[300px] overflow-y-auto pr-2">
              <p className="text-sm text-gray-700 leading-relaxed">
                {showFullText}
              </p>
            </div>
          </div>
        </div>
      )}
      {showMention && filteredTaggable.length > 0 && (
        <div className="absolute bg-white border rounded-lg shadow-md mt-1 w-52 max-h-40 overflow-y-auto z-50">
          {filteredTaggable.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                const newText = replyText.replace(/@[\w\s.]*$/, `@${item.name} `);
                setReplyText(newText);
                setTaggedUsers((prev) => {
                  if (prev.some((u) => u.id === item.id)) return prev;
                  return [...prev, item];
                });
                setShowMention(false);
              }}
              className="px-3 py-2 text-start hover:bg-gray-100 cursor-pointer text-sm flex justify-between items-center"
            >
              <span>{item.name}</span>
              <span className="text-[10px] text-gray-400">{item.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThreadCard;
